import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, UsersThree, Trophy, CaretRight, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function CoachTeams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/coach/teams")
      .then(({ data }) => setTeams(data))
      .catch(() => toast.error("Failed to load teams"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const createTeam = async () => {
    if (!name.trim()) { toast.error("Team name is required"); return; }
    setCreating(true);
    try {
      await api.post("/coach/teams", { name: name.trim(), description: desc.trim() || null });
      toast.success("Team created");
      setName(""); setDesc(""); setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create team");
    } finally {
      setCreating(false);
    }
  };

  const deleteTeam = async (t) => {
    if (!window.confirm(`Delete team "${t.name}"? Members will be removed.`)) return;
    try {
      await api.delete(`/coach/teams/${t.team_id}`);
      toast.success("Team deleted");
      load();
    } catch {
      toast.error("Failed to delete team");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="coach-teams-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny text-brand-accent tracking-widest mb-2">// COACH · TEAMS</div>
          <h1 className="font-heading text-4xl sm:text-5xl">Team Leaderboards</h1>
          <p className="text-text-secondary mt-2 max-w-2xl text-sm">
            Group your active clients into teams and let performance drive them. Rankings auto-update from workouts and new PRs.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-4 py-2.5 text-sm label-tiny tracking-wider hover:brightness-110 transition"
              data-testid="coach-teams-create-btn"
            >
              <Plus size={16} weight="bold" /> NEW TEAM
            </button>
          </DialogTrigger>
          <DialogContent data-testid="coach-teams-create-dialog">
            <DialogHeader>
              <DialogTitle>Create a team</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <label className="text-xs label-tiny text-text-secondary">TEAM NAME</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Morning Squad"
                data-testid="coach-teams-create-name-input"
                maxLength={80}
              />
              <label className="text-xs label-tiny text-text-secondary">DESCRIPTION (OPTIONAL)</label>
              <Textarea
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="What is this team about?"
                data-testid="coach-teams-create-desc-input"
                rows={3}
              />
              <button
                onClick={createTeam}
                disabled={creating}
                className="w-full bg-brand-accent text-surface-base py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60"
                data-testid="coach-teams-create-submit"
              >
                {creating ? "CREATING..." : "CREATE TEAM"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-text-secondary label-tiny" data-testid="coach-teams-loading">Loading teams...</div>
      ) : teams.length === 0 ? (
        <div className="border border-white/10 p-10 text-center" data-testid="coach-teams-empty">
          <UsersThree size={48} className="mx-auto text-brand-accent mb-3" weight="duotone" />
          <div className="font-heading text-xl mb-1">No teams yet</div>
          <div className="text-text-secondary text-sm mb-4">
            Create your first team and start driving performance with private leaderboards.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="coach-teams-list">
          {teams.map((t) => (
            <div
              key={t.team_id}
              className="border border-white/10 hover:border-brand-accent/40 transition p-5 bg-white/[0.02] group"
              data-testid={`coach-team-card-${t.team_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="label-tiny text-brand-accent/80 text-[10px] tracking-widest mb-1">// TEAM</div>
                  <div className="font-heading text-xl truncate">{t.name}</div>
                  {t.description && (
                    <div className="text-text-secondary text-sm mt-1 line-clamp-2">{t.description}</div>
                  )}
                </div>
                <button
                  onClick={() => deleteTeam(t)}
                  className="opacity-0 group-hover:opacity-100 text-text-secondary hover:text-red-400 transition"
                  data-testid={`coach-team-delete-${t.team_id}`}
                  aria-label="Delete team"
                >
                  <Trash size={16} />
                </button>
              </div>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-text-secondary">
                  <UsersThree size={14} /> {t.member_count} member{t.member_count === 1 ? "" : "s"}
                </span>
                <span className="inline-flex items-center gap-1.5 text-brand-accent">
                  <Trophy size={14} /> Live leaderboard
                </span>
              </div>
              <Link
                to={`/coach/teams/${t.team_id}`}
                className="mt-4 inline-flex items-center gap-1 text-sm label-tiny tracking-wider text-white hover:text-brand-accent"
                data-testid={`coach-team-open-${t.team_id}`}
              >
                MANAGE TEAM <CaretRight size={14} weight="bold" />
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
